import React, { useEffect, useState } from "react";
import { Switch, List, Image, message, Skeleton, Button, Space, Tag, Modal } from "antd";
import { fetchEvents, updateEventStatus, deleteEvent } from "../../../../api/events";
import { EditOutlined, DeleteOutlined, CalendarOutlined, EnvironmentOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const EventsList = ({ onEdit }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await fetchEvents();
      if (data) {
        setEvents(data.items || data);
      }
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleToggle = async (id, checked) => {
    try {
      const response = await updateEventStatus(id, checked);
      if (response) {
        setEvents((prev) =>
          prev.map((event) => (event._id === id ? { ...event, isActive: checked } : event))
        );
        message.success("Status updated successfully");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update status");
    }
  };

  const showDeleteModal = (event) => {
    setEventToDelete(event);
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    
    try {
      const response = await deleteEvent(eventToDelete._id);
      if (response) {
        setEvents((prev) => prev.filter((event) => event._id !== eventToDelete._id));
        message.success("Event deleted successfully");
        setDeleteModalVisible(false);
        setEventToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      message.error("Failed to delete event");
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setEventToDelete(null);
  };

  const formatDateTime = (dateTime) => {
    return dayjs(dateTime).format("MMM DD, YYYY HH:mm");
  };

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold mb-4">Events List</h3>
      <List
        dataSource={events}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Space key="actions">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => onEdit && onEdit(item)}
                  title="Edit Event"
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteModal(item)}
                  title="Delete Event"
                />
                <Switch
                  checked={item.isActive}
                  onChange={(checked) => handleToggle(item._id, checked)}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Space>
            ]}
          >
            <List.Item.Meta
              avatar={
                <Image
                  width={120}
                  height={80}
                  src={item.eventImage}
                  alt={item.eventTitle}
                  style={{ objectFit: "cover", borderRadius: "8px" }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
              }
              title={
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-lg">{item.eventTitle}</span>
                  <Tag color={item.isActive ? "green" : "red"}>
                    {item.isActive ? "Active" : "Inactive"}
                  </Tag>
                </div>
              }
              description={
                <div className="space-y-2">
                  <p className="text-gray-600">{item.eventDescription}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <CalendarOutlined className="mr-1" />
                      {formatDateTime(item.eventFullDateTime)}
                    </span>
                    <span className="flex items-center">
                      <EnvironmentOutlined className="mr-1" />
                      {item.eventLocation?.locationName}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Click Action: {item.clickActionType} - {item.clickActionValue}
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
        locale={{
          emptyText: "No events found"
        }}
      />

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={handleCancelDelete}
        okText="Yes"
        cancelText="No"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete the event "{eventToDelete?.eventTitle}"?
        </p>
        <p className="text-gray-500 text-sm mt-2">
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default EventsList;